export function renderSelectedWorks() {
  return `
    <section id="projects" class="cv-auto py-20 md:py-28 px-6 lg:px-16 bg-[#050505] bg-tech-grid relative border-b border-[#1C201E]">
      <!-- Projects Section Header -->
      <div class="max-w-7xl mx-auto mb-16">
        <div class="flex items-center justify-between border-b border-[#1C201E] pb-4 mb-6 font-mono text-xs uppercase tracking-widest text-neutral-400">
          <span class="text-white font-medium">03 / SELECTED WORKS</span>
          <span class="hidden sm:inline tracking-widest text-neutral-400">REAL SYSTEMS, BUILT FROM IDEAS.</span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div class="md:col-span-7">
            <h2 class="text-5xl sm:text-7xl font-bold tracking-tighter text-[#FAF8F5] font-display">
              <span class="sr-only">Featured Projects &amp; Software Engineering Experiments — </span>
              The work behind <br/>
              the <span class="font-serif italic font-normal text-amber-100">experiments.</span>
            </h2>
          </div>
          <div class="md:col-span-5">
            <p class="font-mono text-xs text-neutral-300 leading-relaxed font-light">
              Projects where programming, experimentation and design come together. Not everything starts with a plan. Sometimes it starts with: <strong class="text-white font-medium">"What happens if I build this?"</strong>
            </p>
          </div>
        </div>
      </div>

      <!-- Projects Grid Layout (10 Curated Experiments with CRT Terminal Stages) -->
      <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-purpose="projects-catalog">
        
        <!-- Project 01: Interactive Graphics Playground -->
        <article class="group border border-[#1C201E] bg-[#0E100F] p-6 lg:p-7 rounded-none hover:border-neutral-500 transition-all duration-300 relative flex flex-col justify-between hover:shadow-[0_0_30px_rgba(255,255,255,0.03)]">
          <div>
            <div class="flex justify-between items-center pb-3 border-b border-[#1C201E] font-mono text-xs text-neutral-400 mb-4">
              <span class="flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-neutral-600"></span>
                <span>01 // WEB · GRAPHICS</span>
              </span>
              <span class="text-white font-medium group-hover:text-cyan-telemetry transition-colors">GRAPHICS PLAYGROUND</span>
            </div>

            <div class="h-44 terminal-box rounded-none border border-[#1C201E] p-4 font-mono text-[10px] text-neutral-400 overflow-hidden relative flex flex-col justify-between scanlines">
              <div class="flex justify-between items-center text-neutral-400 z-10">
                <span class="flex items-center gap-1.5">
                  <span class="text-neutral-500">PIPELINE:</span>
                  <span class="text-neutral-200 font-bold">WEBGL2 RASTER</span>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-cyan-950/70 border border-cyan-500/40 text-[8.5px] text-cyan-300 font-bold ml-1">
                    <span class="w-1.5 h-1.5 rounded-full bg-cyan-400 terminal-beacon-cyan"></span>
                    <span>ACTIVE</span>
                  </span>
                </span>
                <span class="flex items-center gap-2 font-semibold text-cyan-300 tracking-wide">
                  <span class="relative flex h-2.5 w-2.5 items-center justify-center">
                    <span class="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-80 animate-ping"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-cyan-300 terminal-beacon-cyan"></span>
                  </span>
                  <span class="terminal-blink-text font-bold">FPS:</span>
                  <span class="text-white">60 LOCKED</span>
                  <span class="terminal-cursor text-cyan-400 font-bold -ml-1">_</span>
                </span>
              </div>

              <!-- CRT GLSL Shimmer -->
              <div class="text-center my-auto z-10">
                <div class="font-mono text-xs text-cyan-400 font-bold tracking-widest uppercase">
                  RAYMARCHING &amp; REAL-TIME SHADERS
                </div>
                <span class="text-[9px] text-neutral-400 block mt-1">
                  vec4 col = renderScene(rayOrigin, rayDir);
                </span>
              </div>

              <div class="flex justify-between items-center text-[9px] text-neutral-400 border-t border-[#1C201E] pt-2 z-10">
                <span class="text-neutral-500">BUFFER: <span class="text-neutral-200">FBO DUAL PASS</span></span>
                <span class="text-cyan-300 font-bold">VITE HMR</span>
              </div>
            </div>

            <h3 class="text-xl font-bold text-white mt-5 uppercase tracking-tight font-display">Interactive Graphics Playground</h3>
            <p class="mt-2 text-xs text-neutral-400 font-light leading-relaxed">
              Experiments with WebGL, shaders and real-time graphics exploring procedural generation, optical lighting, and direct GPU computing.
            </p>
          </div>

          <div class="mt-6 pt-4 border-t border-[#1C201E] font-mono text-[11px]">
            <span class="text-neutral-400 uppercase">Code · WebGL · GLSL · Vite</span>
          </div>
        </article>

        <!-- Project 02: Godot Experiments -->
        <article class="group border border-[#1C201E] bg-[#0E100F] p-6 lg:p-7 rounded-none hover:border-neutral-500 transition-all duration-300 relative flex flex-col justify-between hover:shadow-[0_0_30px_rgba(255,255,255,0.03)]">
          <div>
            <div class="flex justify-between items-center pb-3 border-b border-[#1C201E] font-mono text-xs text-neutral-400 mb-4">
              <span class="flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-neutral-600"></span>
                <span>02 // GAME · 3D</span>
              </span>
              <span class="text-white font-medium group-hover:text-amber-200 transition-colors">GODOT 3D LAB</span>
            </div>

            <div class="h-44 terminal-box rounded-none border border-[#1C201E] p-4 font-mono text-[10px] text-neutral-400 overflow-hidden relative flex flex-col justify-between scanlines">
              <div class="flex justify-between items-center text-neutral-400 z-10">
                <span class="flex items-center gap-1.5">
                  <span class="text-neutral-500">ENGINE:</span>
                  <span class="text-neutral-200 font-bold">GODOT 4.X</span>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-950/70 border border-amber-500/40 text-[8.5px] text-amber-300 font-bold ml-1">
                    <span class="w-1.5 h-1.5 rounded-full bg-amber-400 terminal-beacon-amber"></span>
                    <span>PHYSICS</span>
                  </span>
                </span>
                <span class="flex items-center gap-2 font-semibold text-amber-300 tracking-wide">
                  <span class="relative flex h-2.5 w-2.5 items-center justify-center">
                    <span class="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-80 animate-ping"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-300 terminal-beacon-amber"></span>
                  </span>
                  <span class="terminal-blink-text font-bold">TICKS:</span>
                  <span class="text-white font-mono">60 TPS</span>
                  <span class="terminal-cursor text-amber-400 font-bold -ml-1">_</span>
                </span>
              </div>

              <!-- 3D Physics Simulation Telemetry -->
              <div class="font-mono text-[9px] text-neutral-300 space-y-1 my-auto px-2 z-10">
                <div class="flex justify-between">
                  <span class="text-neutral-500">RIGIDBODY_3D:</span>
                  <span class="text-amber-300">COLLISION_LAYER_1</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-neutral-500">VELOCITY_VECTOR:</span>
                  <span class="text-neutral-200">(12.4, 0.0, -8.1)</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-neutral-500">CHARACTER_BODY:</span>
                  <span class="text-emerald-400">STATE: GROUNDED</span>
                </div>
              </div>

              <div class="flex justify-between items-center text-[9px] text-neutral-400 border-t border-[#1C201E] pt-2 z-10">
                <span class="text-neutral-500">LANG: <span class="text-neutral-200">GDSCRIPT</span></span>
                <span class="text-amber-300 font-bold">3D SPATIAL</span>
              </div>
            </div>

            <h3 class="text-xl font-bold text-white mt-5 uppercase tracking-tight font-display">Godot Experiments</h3>
            <p class="mt-2 text-xs text-neutral-400 font-light leading-relaxed">
              Game-development experiments exploring 3D environments, physics, gameplay systems and interactive mechanics in Godot.
            </p>
          </div>

          <div class="mt-6 pt-4 border-t border-[#1C201E] font-mono text-[11px]">
            <span class="text-neutral-400 uppercase">Godot · GDScript · 3D · Physics</span>
          </div>
        </article>

        <!-- Project 03: Personal Productivity System -->
        <article class="group border border-[#1C201E] bg-[#0E100F] p-6 lg:p-7 rounded-none hover:border-neutral-500 transition-all duration-300 relative flex flex-col justify-between hover:shadow-[0_0_30px_rgba(255,255,255,0.03)]">
          <div>
            <div class="flex justify-between items-center pb-3 border-b border-[#1C201E] font-mono text-xs text-neutral-400 mb-4">
              <span class="flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-neutral-600"></span>
                <span>03 // DESKTOP · SYSTEM</span>
              </span>
              <span class="text-white font-medium group-hover:text-cyan-telemetry transition-colors">PRODUCTIVITY OS</span>
            </div>

            <div class="h-44 terminal-box rounded-none border border-[#1C201E] p-4 font-mono text-[10px] text-neutral-400 overflow-hidden relative flex flex-col justify-between scanlines">
              <div class="flex justify-between items-center text-neutral-400 z-10">
                <span class="flex items-center gap-1.5">
                  <span class="text-neutral-500">PLATFORM:</span>
                  <span class="text-neutral-200">ELECTRON DESKTOP</span>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-cyan-950/70 border border-cyan-500/40 text-[8.5px] text-cyan-300 font-bold ml-1">
                    <span class="w-1.5 h-1.5 rounded-full bg-cyan-400 terminal-beacon-cyan"></span>
                    <span>LOCAL</span>
                  </span>
                </span>
                <span class="flex items-center gap-2 font-semibold text-cyan-300 tracking-wide">
                  <span class="relative flex h-2.5 w-2.5 items-center justify-center">
                    <span class="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-80 animate-ping"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-cyan-300 terminal-beacon-cyan"></span>
                  </span>
                  <span class="terminal-blink-text font-bold">STATE:</span>
                  <span class="text-white">ENCRYPTED</span>
                  <span class="terminal-cursor text-cyan-400 font-bold -ml-1">_</span>
                </span>
              </div>

              <!-- Modular Productivity Pillars -->
              <div class="grid grid-cols-3 gap-2 my-auto z-10 text-center font-mono text-[9px]">
                <div class="border border-[#1C201E] bg-[#050505]/90 p-1.5">
                  <span class="text-neutral-500 block">TASKS</span>
                  <span class="text-cyan-300 font-bold">ACTIVE</span>
                </div>
                <div class="border border-[#1C201E] bg-[#050505]/90 p-1.5">
                  <span class="text-neutral-500 block">GOALS</span>
                  <span class="text-emerald-400 font-bold">TRACKED</span>
                </div>
                <div class="border border-[#1C201E] bg-[#050505]/90 p-1.5">
                  <span class="text-neutral-500 block">FINANCES</span>
                  <span class="text-amber-300 font-bold">LOCKED</span>
                </div>
              </div>

              <div class="flex justify-between items-center text-[9px] text-neutral-400 border-t border-[#1C201E] pt-2 z-10">
                <span class="text-neutral-500">STORAGE: <span class="text-neutral-200">OFFLINE FIRST</span></span>
                <span class="text-cyan-300 font-bold">ZERO CLOUD LATENCY</span>
              </div>
            </div>

            <h3 class="text-xl font-bold text-white mt-5 uppercase tracking-tight font-display">Personal Productivity System</h3>
            <p class="mt-2 text-xs text-neutral-400 font-light leading-relaxed">
              A desktop application designed around tasks, projects, goals, notes, finances and personal organization with keyboard-first ergonomics.
            </p>
          </div>

          <div class="mt-6 pt-4 border-t border-[#1C201E] font-mono text-[11px]">
            <span class="text-neutral-400 uppercase">Electron · JavaScript · Local Data</span>
          </div>
        </article>

        <!-- Project 04: Three.js Experiments -->
        <article class="group border border-[#1C201E] bg-[#0E100F] p-6 lg:p-7 rounded-none hover:border-neutral-500 transition-all duration-300 relative flex flex-col justify-between hover:shadow-[0_0_30px_rgba(255,255,255,0.03)]">
          <div>
            <div class="flex justify-between items-center pb-3 border-b border-[#1C201E] font-mono text-xs text-neutral-400 mb-4">
              <span class="flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-neutral-600"></span>
                <span>04 // WEB · 3D</span>
              </span>
              <span class="text-white font-medium group-hover:text-emerald-300 transition-colors">THREE.JS LAB</span>
            </div>

            <div class="h-44 terminal-box rounded-none border border-[#1C201E] p-4 font-mono text-[10px] text-neutral-400 overflow-hidden relative flex flex-col justify-between scanlines">
              <div class="flex justify-between items-center text-neutral-400 z-10">
                <span class="flex items-center gap-1.5">
                  <span class="text-neutral-500">SCENE GRAPH:</span>
                  <span class="text-neutral-200">PROCEDURAL MESH</span>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-950/70 border border-emerald-500/40 text-[8.5px] text-emerald-300 font-bold ml-1">
                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 terminal-beacon-green"></span>
                    <span>PBR</span>
                  </span>
                </span>
                <span class="flex items-center gap-2 font-semibold text-emerald-300 tracking-wide">
                  <span class="relative flex h-2.5 w-2.5 items-center justify-center">
                    <span class="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-80 animate-ping"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-300 terminal-beacon-green"></span>
                  </span>
                  <span class="terminal-blink-text font-bold">CAMERA:</span>
                  <span class="text-white">RAYCAST INTERTIA</span>
                  <span class="terminal-cursor text-emerald-400 font-bold -ml-1">_</span>
                </span>
              </div>

              <!-- Monospace Mesh Wireframe Display -->
              <div class="my-auto text-center z-10 font-mono text-[9px] text-emerald-300/90 leading-tight select-none">
                <pre class="inline-block text-left text-[8px] text-emerald-400/80">
    ▲         ▲
   / \\       / \\      [3D PROCEDURAL STAGE]
  /___\\_____/___\\     Keycaps · Guitar · Headset
  \\   /     \\   /     Normal Maps · PointLight
   \\v/       \\v/
                </pre>
              </div>

              <div class="flex justify-between items-center text-[9px] text-neutral-400 border-t border-[#1C201E] pt-2 z-10">
                <span class="text-neutral-500">SHADOWS: <span class="text-neutral-200">PCF SOFT</span></span>
                <span class="text-emerald-300 font-bold">THREE.JS R160</span>
              </div>
            </div>

            <h3 class="text-xl font-bold text-white mt-5 uppercase tracking-tight font-display">Three.js Experiments</h3>
            <p class="mt-2 text-xs text-neutral-400 font-light leading-relaxed">
              Interactive 3D experiences built for the browser. Featuring procedural mesh generation, custom lighting pipelines, and responsive cursor physics.
            </p>
          </div>

          <div class="mt-6 pt-4 border-t border-[#1C201E] font-mono text-[11px]">
            <span class="text-neutral-400 uppercase">Three.js · WebGL · JavaScript</span>
          </div>
        </article>

        <!-- Project 05: Blender Experiments -->
        <article class="group border border-[#1C201E] bg-[#0E100F] p-6 lg:p-7 rounded-none hover:border-neutral-500 transition-all duration-300 relative flex flex-col justify-between hover:shadow-[0_0_30px_rgba(255,255,255,0.03)]">
          <div>
            <div class="flex justify-between items-center pb-3 border-b border-[#1C201E] font-mono text-xs text-neutral-400 mb-4">
              <span class="flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-neutral-600"></span>
                <span>05 // CREATIVE · 3D</span>
              </span>
              <span class="text-white font-medium group-hover:text-amber-200 transition-colors">BLENDER 3D VFX</span>
            </div>

            <div class="h-44 terminal-box rounded-none border border-[#1C201E] p-4 font-mono text-[10px] text-neutral-400 overflow-hidden relative flex flex-col justify-between scanlines">
              <div class="flex justify-between items-center text-neutral-400 z-10">
                <span class="flex items-center gap-1.5">
                  <span class="text-neutral-500">WORKFLOW:</span>
                  <span class="text-neutral-200">SUBD &amp; HARD SURFACE</span>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-950/70 border border-amber-500/40 text-[8.5px] text-amber-300 font-bold ml-1">
                    <span class="w-1.5 h-1.5 rounded-full bg-amber-400 terminal-beacon-amber"></span>
                    <span>CYCLES</span>
                  </span>
                </span>
                <span class="flex items-center gap-2 font-semibold text-amber-300 tracking-wide">
                  <span class="relative flex h-2.5 w-2.5 items-center justify-center">
                    <span class="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-80 animate-ping"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-300 terminal-beacon-amber"></span>
                  </span>
                  <span class="terminal-blink-text font-bold">RENDER:</span>
                  <span class="text-white">RAYTRACED</span>
                  <span class="terminal-cursor text-amber-400 font-bold -ml-1">_</span>
                </span>
              </div>

              <!-- Node Network Wireframe -->
              <div class="my-auto text-center z-10 font-mono text-[9px] text-amber-100">
                <span class="font-display text-xl uppercase tracking-widest block text-neutral-200">
                  PROCEDURAL NODES &amp; RIGS
                </span>
                <span class="text-[9px] text-neutral-400 block mt-0.5">Geometry Nodes · Volumetrics · Lighting</span>
              </div>

              <div class="flex justify-between items-center text-[9px] text-neutral-400 border-t border-[#1C201E] pt-2 z-10">
                <span class="text-neutral-500">TOPOLOGY: <span class="text-neutral-200">QUAD BASED</span></span>
                <span class="text-amber-300 font-bold">ANIMATION READY</span>
              </div>
            </div>

            <h3 class="text-xl font-bold text-white mt-5 uppercase tracking-tight font-display">Blender Experiments</h3>
            <p class="mt-2 text-xs text-neutral-400 font-light leading-relaxed">
              3D modeling, animation, VFX and procedural experimentation focusing on physical lighting, materials, and form sculpting.
            </p>
          </div>

          <div class="mt-6 pt-4 border-t border-[#1C201E] font-mono text-[11px]">
            <span class="text-neutral-400 uppercase">Blender · 3D · VFX</span>
          </div>
        </article>

        <!-- Project 06: React Native Experiments -->
        <article class="group border border-[#1C201E] bg-[#0E100F] p-6 lg:p-7 rounded-none hover:border-neutral-500 transition-all duration-300 relative flex flex-col justify-between hover:shadow-[0_0_30px_rgba(255,255,255,0.03)]">
          <div>
            <div class="flex justify-between items-center pb-3 border-b border-[#1C201E] font-mono text-xs text-neutral-400 mb-4">
              <span class="flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-neutral-600"></span>
                <span>06 // MOBILE · APPLICATION</span>
              </span>
              <span class="text-white font-medium group-hover:text-cyan-telemetry transition-colors">MOBILE UX LAB</span>
            </div>

            <div class="h-44 terminal-box rounded-none border border-[#1C201E] p-4 font-mono text-[10px] text-neutral-400 overflow-hidden relative flex flex-col justify-between scanlines">
              <div class="flex justify-between items-center text-neutral-400 z-10">
                <span class="flex items-center gap-1.5">
                  <span class="text-neutral-500">TARGET:</span>
                  <span class="text-neutral-200 font-bold">IOS / ANDROID</span>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-cyan-950/70 border border-cyan-500/40 text-[8.5px] text-cyan-300 font-bold ml-1">
                    <span class="w-1.5 h-1.5 rounded-full bg-cyan-400 terminal-beacon-cyan"></span>
                    <span>HERMES</span>
                  </span>
                </span>
                <span class="flex items-center gap-2 font-semibold text-cyan-300 tracking-wide">
                  <span class="relative flex h-2.5 w-2.5 items-center justify-center">
                    <span class="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-80 animate-ping"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-cyan-300 terminal-beacon-cyan"></span>
                  </span>
                  <span class="terminal-blink-text font-bold">GESTURE:</span>
                  <span class="text-white">60 FPS TOUCH</span>
                  <span class="terminal-cursor text-cyan-400 font-bold -ml-1">_</span>
                </span>
              </div>

              <!-- Mobile Device Blueprint Simulation -->
              <div class="my-auto z-10 text-center font-mono text-[9px] text-neutral-300">
                <span class="border border-neutral-700 px-3 py-1 bg-neutral-900/90 inline-block text-cyan-300 font-bold">
                  [ RESPONSIVE TOUCH ENGINE ]
                </span>
                <span class="text-[8.5px] text-neutral-500 block mt-1.5">Fluid Bottom Sheets · Spring Physics</span>
              </div>

              <div class="flex justify-between items-center text-[9px] text-neutral-400 border-t border-[#1C201E] pt-2 z-10">
                <span class="text-neutral-500">BRIDGE: <span class="text-neutral-200">JSI NATIVE</span></span>
                <span class="text-cyan-300 font-bold">REACT NATIVE</span>
              </div>
            </div>

            <h3 class="text-xl font-bold text-white mt-5 uppercase tracking-tight font-display">React Native Experiments</h3>
            <p class="mt-2 text-xs text-neutral-400 font-light leading-relaxed">
              Mobile interfaces and application experiments built around modern JavaScript development with smooth native gesture response.
            </p>
          </div>

          <div class="mt-6 pt-4 border-t border-[#1C201E] font-mono text-[11px]">
            <span class="text-neutral-400 uppercase">React Native · JavaScript · UI</span>
          </div>
        </article>

        <!-- Project 07: Full-Stack Experiments -->
        <article class="group border border-[#1C201E] bg-[#0E100F] p-6 lg:p-7 rounded-none hover:border-neutral-500 transition-all duration-300 relative flex flex-col justify-between hover:shadow-[0_0_30px_rgba(255,255,255,0.03)]">
          <div>
            <div class="flex justify-between items-center pb-3 border-b border-[#1C201E] font-mono text-xs text-neutral-400 mb-4">
              <span class="flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-neutral-600"></span>
                <span>07 // WEB · FULL STACK</span>
              </span>
              <span class="text-white font-medium group-hover:text-emerald-300 transition-colors">FULL-STACK LAB</span>
            </div>

            <div class="h-44 terminal-box rounded-none border border-[#1C201E] p-4 font-mono text-[10px] text-neutral-400 overflow-hidden relative flex flex-col justify-between scanlines">
              <div class="flex justify-between items-center text-neutral-400 z-10">
                <span class="flex items-center gap-1.5">
                  <span class="text-neutral-500">DATABASE:</span>
                  <span class="text-neutral-200 font-bold">MONGODB &amp; FIREBASE</span>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-950/70 border border-emerald-500/40 text-[8.5px] text-emerald-300 font-bold ml-1">
                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 terminal-beacon-green"></span>
                    <span>SYNC</span>
                  </span>
                </span>
                <span class="flex items-center gap-2 font-semibold text-emerald-300 tracking-wide">
                  <span class="relative flex h-2.5 w-2.5 items-center justify-center">
                    <span class="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-80 animate-ping"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-300 terminal-beacon-green"></span>
                  </span>
                  <span class="terminal-blink-text font-bold">API:</span>
                  <span class="text-white">200 OK</span>
                  <span class="terminal-cursor text-emerald-400 font-bold -ml-1">_</span>
                </span>
              </div>

              <!-- Endpoint Telemetry Buffer -->
              <div class="font-mono text-[9px] space-y-1.5 my-auto px-2 z-10">
                <div class="flex items-center justify-between text-neutral-400">
                  <span>POST /api/v1/sync</span>
                  <span class="text-emerald-300 font-bold">14ms</span>
                </div>
                <div class="flex items-center justify-between text-neutral-400">
                  <span>WSS /stream/realtime</span>
                  <span class="text-cyan-300 font-bold">ESTABLISHED</span>
                </div>
              </div>

              <div class="flex justify-between items-center text-[9px] text-neutral-400 border-t border-[#1C201E] pt-2 z-10">
                <span class="text-neutral-500">BACKEND: <span class="text-neutral-200">NODE.JS RUNTIME</span></span>
                <span class="text-emerald-300 font-bold">REST &amp; SOCKETS</span>
              </div>
            </div>

            <h3 class="text-xl font-bold text-white mt-5 uppercase tracking-tight font-display">Full-Stack Experiments</h3>
            <p class="mt-2 text-xs text-neutral-400 font-light leading-relaxed">
              Applications connecting modern frontend interfaces with APIs, databases and resilient backend infrastructure.
            </p>
          </div>

          <div class="mt-6 pt-4 border-t border-[#1C201E] font-mono text-[11px]">
            <span class="text-neutral-400 uppercase">React · Node.js · MongoDB · Firebase</span>
          </div>
        </article>

        <!-- Project 08: Shader Experiments -->
        <article class="group border border-[#1C201E] bg-[#0E100F] p-6 lg:p-7 rounded-none hover:border-neutral-500 transition-all duration-300 relative flex flex-col justify-between hover:shadow-[0_0_30px_rgba(255,255,255,0.03)]">
          <div>
            <div class="flex justify-between items-center pb-3 border-b border-[#1C201E] font-mono text-xs text-neutral-400 mb-4">
              <span class="flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-neutral-600"></span>
                <span>08 // GRAPHICS · SHADERS</span>
              </span>
              <span class="text-white font-medium group-hover:text-amber-200 transition-colors">GLSL SHADER LAB</span>
            </div>

            <div class="h-44 terminal-box rounded-none border border-[#1C201E] p-4 font-mono text-[10px] text-neutral-400 overflow-hidden relative flex flex-col justify-between scanlines">
              <div class="flex justify-between items-center text-neutral-400 z-10">
                <span class="flex items-center gap-1.5">
                  <span class="text-neutral-500">SHADER:</span>
                  <span class="text-neutral-200 font-bold">FRAGMENT STAGE</span>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-950/70 border border-amber-500/40 text-[8.5px] text-amber-300 font-bold ml-1">
                    <span class="w-1.5 h-1.5 rounded-full bg-amber-400 terminal-beacon-amber"></span>
                    <span>COMPILED</span>
                  </span>
                </span>
                <span class="flex items-center gap-2 font-semibold text-amber-300 tracking-wide">
                  <span class="relative flex h-2.5 w-2.5 items-center justify-center">
                    <span class="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-80 animate-ping"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-300 terminal-beacon-amber"></span>
                  </span>
                  <span class="terminal-blink-text font-bold">PIXELS:</span>
                  <span class="text-white">1080P PROCESSED</span>
                  <span class="terminal-cursor text-amber-400 font-bold -ml-1">_</span>
                </span>
              </div>

              <!-- Real Shader Code Fragment -->
              <div class="font-mono text-[9px] text-neutral-300 leading-relaxed bg-[#050505]/95 p-2 border border-[#1C201E] z-10">
                <div class="flex items-center gap-2">
                  <span class="text-neutral-600 select-none">01</span>
                  <code><span class="text-cyan-400">vec2</span> uv = gl_FragCoord.xy / u_resolution.xy;</code>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-neutral-600 select-none">02</span>
                  <code><span class="text-amber-300">float</span> noise = fract(sin(dot(uv, vec2(12.9898, 78.233))) * 43758.54);</code>
                </div>
              </div>

              <div class="flex justify-between items-center text-[9px] text-neutral-400 border-t border-[#1C201E] pt-2 z-10">
                <span class="text-neutral-500">MATH: <span class="text-neutral-200">TRIGONOMETRIC NOISE</span></span>
                <span class="text-amber-300 font-bold">REAL-TIME</span>
              </div>
            </div>

            <h3 class="text-xl font-bold text-white mt-5 uppercase tracking-tight font-display">Shader Experiments</h3>
            <p class="mt-2 text-xs text-neutral-400 font-light leading-relaxed">
              Exploring how mathematics and code can create visual texture, organic turbulence, and geometric light directly in real time.
            </p>
          </div>

          <div class="mt-6 pt-4 border-t border-[#1C201E] font-mono text-[11px]">
            <span class="text-neutral-400 uppercase">GLSL · WebGL · Graphics</span>
          </div>
        </article>

        <!-- Project 09: Programming Experiments -->
        <article class="group border border-[#1C201E] bg-[#0E100F] p-6 lg:p-7 rounded-none hover:border-neutral-500 transition-all duration-300 relative flex flex-col justify-between hover:shadow-[0_0_30px_rgba(255,255,255,0.03)]">
          <div>
            <div class="flex justify-between items-center pb-3 border-b border-[#1C201E] font-mono text-xs text-neutral-400 mb-4">
              <span class="flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-neutral-600"></span>
                <span>09 // SYSTEMS · CODE</span>
              </span>
              <span class="text-white font-medium group-hover:text-cyan-telemetry transition-colors">ALGORITHM &amp; SYSTEMS</span>
            </div>

            <div class="h-44 terminal-box rounded-none border border-[#1C201E] p-4 font-mono text-[10px] text-neutral-400 overflow-hidden relative flex flex-col justify-between scanlines">
              <div class="flex justify-between items-center text-neutral-400 z-10">
                <span class="flex items-center gap-1.5">
                  <span class="text-neutral-500">MEMORY:</span>
                  <span class="text-neutral-200 font-bold">HEAP ALLOCATED</span>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-cyan-950/70 border border-cyan-500/40 text-[8.5px] text-cyan-300 font-bold ml-1">
                    <span class="w-1.5 h-1.5 rounded-full bg-cyan-400 terminal-beacon-cyan"></span>
                    <span>STABLE</span>
                  </span>
                </span>
                <span class="flex items-center gap-2 font-semibold text-cyan-300 tracking-wide">
                  <span class="relative flex h-2.5 w-2.5 items-center justify-center">
                    <span class="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-80 animate-ping"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-cyan-300 terminal-beacon-cyan"></span>
                  </span>
                  <span class="terminal-blink-text font-bold">COMPLEXITY:</span>
                  <span class="text-white">O(log N)</span>
                  <span class="terminal-cursor text-cyan-400 font-bold -ml-1">_</span>
                </span>
              </div>

              <!-- Low-Level Systems Code Buffer -->
              <div class="font-mono text-[9px] text-neutral-300 leading-relaxed bg-[#050505]/95 p-2 border border-[#1C201E] z-10">
                <div class="flex items-center gap-2">
                  <span class="text-neutral-600 select-none">01</span>
                  <code><span class="text-cyan-400">void*</span> ptr = malloc(sizeof(Node) * 1024);</code>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-neutral-600 select-none">02</span>
                  <code><span class="text-amber-300">assert</span>(ptr != NULL); <span class="text-neutral-500">// zero leakage</span></code>
                </div>
              </div>

              <div class="flex justify-between items-center text-[9px] text-neutral-400 border-t border-[#1C201E] pt-2 z-10">
                <span class="text-neutral-500">LANGS: <span class="text-neutral-200">C · C++ · C# · PYTHON</span></span>
                <span class="text-cyan-300 font-bold">DATA STRUCTURES</span>
              </div>
            </div>

            <h3 class="text-xl font-bold text-white mt-5 uppercase tracking-tight font-display">Programming Experiments</h3>
            <p class="mt-2 text-xs text-neutral-400 font-light leading-relaxed">
              Small systems, algorithms and programming projects built while exploring C, C++, C#, Python and JavaScript.
            </p>
          </div>

          <div class="mt-6 pt-4 border-t border-[#1C201E] font-mono text-[11px]">
            <span class="text-neutral-400 uppercase">C · C++ · C# · Python · JavaScript</span>
          </div>
        </article>

        <!-- Project 10: Interactive Systems -->
        <article class="group border border-[#1C201E] bg-[#0E100F] p-6 lg:p-7 rounded-none hover:border-neutral-500 transition-all duration-300 relative flex flex-col justify-between hover:shadow-[0_0_30px_rgba(255,255,255,0.03)]">
          <div>
            <div class="flex justify-between items-center pb-3 border-b border-[#1C201E] font-mono text-xs text-neutral-400 mb-4">
              <span class="flex items-center gap-1.5">
                <span class="w-1.5 h-1.5 rounded-full bg-neutral-600"></span>
                <span>10 // GAME · ENGINE</span>
              </span>
              <span class="text-white font-medium group-hover:text-amber-200 transition-colors">GAMEPLAY SYSTEMS</span>
            </div>

            <div class="h-44 terminal-box rounded-none border border-[#1C201E] p-4 font-mono text-[10px] text-neutral-400 overflow-hidden relative flex flex-col justify-between scanlines">
              <div class="flex justify-between items-center text-neutral-400 z-10">
                <span class="flex items-center gap-1.5">
                  <span class="text-neutral-500">INPUT:</span>
                  <span class="text-neutral-200 font-bold">EVENT DISPATCHER</span>
                  <span class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-950/70 border border-amber-500/40 text-[8.5px] text-amber-300 font-bold ml-1">
                    <span class="w-1.5 h-1.5 rounded-full bg-amber-400 terminal-beacon-amber"></span>
                    <span>LOOP</span>
                  </span>
                </span>
                <span class="flex items-center gap-2 font-semibold text-amber-300 tracking-wide">
                  <span class="relative flex h-2.5 w-2.5 items-center justify-center">
                    <span class="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-80 animate-ping"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-amber-300 terminal-beacon-amber"></span>
                  </span>
                  <span class="terminal-blink-text font-bold">DELTA:</span>
                  <span class="text-white">16.66MS FIXED</span>
                  <span class="terminal-cursor text-amber-400 font-bold -ml-1">_</span>
                </span>
              </div>

              <!-- Gameplay Logic Telemetry -->
              <div class="my-auto z-10 text-center font-mono text-[9px] text-amber-100">
                <span class="text-neutral-200 font-bold block text-sm tracking-widest uppercase">
                  KINEMATIC &amp; INPUT PIPELINES
                </span>
                <span class="text-[8.5px] text-neutral-500 block mt-1">State Machines · Physics Constraints · Ray Colliders</span>
              </div>

              <div class="flex justify-between items-center text-[9px] text-neutral-400 border-t border-[#1C201E] pt-2 z-10">
                <span class="text-neutral-500">SYSTEM: <span class="text-neutral-200">GODOT ENGINE</span></span>
                <span class="text-amber-300 font-bold">REAL-TIME INTERACTION</span>
              </div>
            </div>

            <h3 class="text-xl font-bold text-white mt-5 uppercase tracking-tight font-display">Interactive Systems</h3>
            <p class="mt-2 text-xs text-neutral-400 font-light leading-relaxed">
              Experiments with gameplay logic, physics, input systems and real-time interaction built to test mechanical feel and feedback.
            </p>
          </div>

          <div class="mt-6 pt-4 border-t border-[#1C201E] font-mono text-[11px]">
            <span class="text-neutral-400 uppercase">Godot · Systems · Physics</span>
          </div>
        </article>

      </div>

      <!-- Complete Archive Action Button -->
      <div class="max-w-7xl mx-auto mt-16 text-center">
        <a class="inline-flex items-center gap-2 border border-white/20 hover:border-white hover:bg-white hover:text-black px-8 py-3.5 font-mono text-xs text-white transition-all duration-150 rounded-none uppercase tracking-widest font-semibold" href="https://github.com/Ranjika123" target="_blank" rel="noopener noreferrer">
          <span>EXPLORE FULL ARCHIVE ON GITHUB</span>
          <span>↗</span>
        </a>
      </div>
    </section>
  `;
}
